import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { CustomerBusinessDocument } from '@/libs/fleet-api/services/types';
import {
	Alert,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import { FC, Fragment, useState } from 'react';
import { useFRMPraSurveyInput } from './PraSurveyInputPage';
import {
	// EditOutlined,
	LoadingOutlined,
	FileSearchOutlined,
	UploadOutlined,
	EditOutlined,
} from '@ant-design/icons';
import FleetModal from '@/libs/antd/components/Modal';
import rule from '@/libs/antd/rules';
import dayjs from 'dayjs';
import { DefaultOptionType } from 'antd/es/select';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import DataRow, { DataRowItem } from '@/libs/antd/components/DataRow';
import { useModals } from '@/components/modals/ModalProvider';
import { FleetFile } from '@/libs/fleet-api/fetcher';
// import { useApp } from '@/App';

type FRMPraSurveyInputBusinessDocumentFormProps = {
	sectorIndustry?: string;
	industryClass?: string;
	valid?: boolean;
	customerId?: string;
};

type BusinessDocumentItem = CustomerBusinessDocument<'payload'> &
	Pick<
		CustomerBusinessDocument,
		| 'documentType'
		| 'isMandatory'
		| 'file'
		| 'publisher'
		| 'publishers'
		| 'validityPeriod'
		| 'validityPeriods'
		| 'isDocumentNoMandatory'
	>;

type BusinessDocumentForm = {
	input?: BusinessDocumentItem;
	items: BusinessDocumentItem[];
	isSurveyByBankMandiri: boolean;
	bankMandiriSurveyAt: string;
};

export const useFRMPraSurveyInputBusinessDocumentForm = () => {
	return useForm<BusinessDocumentForm>();
};

const FRMPraSurveyInputBusinessDocumentForm: FC<
	FRMPraSurveyInputBusinessDocumentFormProps
> = ({
	sectorIndustry = '',
	industryClass = '',
	valid = false,
	customerId = '',
}) => {
	const { businessDocumentForm, customerBusinessDocuments, mainInformation } =
		useFRMPraSurveyInput();

	const { form, fieldName } = businessDocumentForm;
	const [activeIndexModal, setActiveIndexModal] = useState<number>();

	// const { navigate } = useApp();
	const { openPreviewModal } = useModals();

	const handleSubmit = ({ input }: BusinessDocumentForm) => {
		const items = form.getFieldValue(
			fieldName('items'),
		) as BusinessDocumentItem[];

		form.setFieldsValue({
			items: items.map((item, index) => {
				if (index === activeIndexModal) return input;
				return item;
			}),
		} as BusinessDocumentForm);

		setActiveIndexModal(undefined);
		form.resetFields([fieldName(['input'])]);
	};

	const getInitialValues = () => {
		return {
			items:
				customerBusinessDocuments?.map((data) => {
					return {
						id: data.id,
						documentNo: data.documentNo,
						documentDate: data.documentDate,
						documentType: data.documentType,
						file: data.file,
						isMandatory: data.isMandatory,
						publisherId: data.publisher?.id,
						publishers: data.publishers,
						validityPeriodId: data.validityPeriod?.id,
						validityPeriods: data.validityPeriods,
						validityPeriod: data.validityPeriod,
						publisher: data.publisher,
					} as BusinessDocumentItem;
				}) ?? [],
			input: undefined,
		} as BusinessDocumentForm;
	};

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const sectorIndustryItems: DataRowItem[] = [
		[
			['Sektor Industri Level 5', sectorIndustry],
			['Kelas Industri', industryClass],
		],
	];

	return (
		<Form.Provider>
			<FleetTitle>Data Perizinan Usaha</FleetTitle>

			<DataRow items={sectorIndustryItems} />
			<Divider />
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={getInitialValues()}
				onFinish={handleSubmit}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['items'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue, resetFields }) => {
						const values = getFieldsValue() as BusinessDocumentForm;

						return (
							<Fragment>
								{!valid && (
									<Alert
										message="Mohon lengkapi seluruh dokumen perizinan usaha yang wajib (*)"
										type="warning"
										showIcon
										className="custom-alert"
										style={{ marginBottom: 20 }}
									/>
								)}
								<Table
									bordered
									dataSource={
										values?.items?.map((item, key) => {
											return { ...item, key };
										}) ?? []
									}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											width: 1,
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<a
																onClick={(e) => {
																	e.preventDefault();
																	if (!record.file?.id) return;
																	openPreviewModal(record.file as FleetFile);
																}}
																style={
																	record.file?.url
																		? { display: 'block' }
																		: {
																				pointerEvents: 'none',
																				color: '#bababa',
																				display: 'block',
																			}
																}
															>
																<FileSearchOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setActiveIndexModal(index);
																}}
															>
																<EditOutlined />
															</a>
														</div>
													</Row>
												);
											},
										},
										{
											title: 'Jenis Dokumen',
											render: (_, record) => {
												return (
													<>
														{record.documentType}
														{(record.isMandatory ||
															record.documentType.indexOf('(Jika Ada)') !==
																-1) && <span style={{ color: 'red' }}>*</span>}
													</>
												);
											},
										},
										{
											title: 'No Dokumen',
											render: (_, record) => record.documentNo ?? '-',
										},
										{
											title: 'Penerbit',
											render: (_, record) => record.publisher?.value ?? '-',
										},
										{
											title: 'Tanggal Dokumen',
											render: (_, record) =>
												record.documentDate
													? dayjs(record.documentDate).format('YYYY-MM-DD')
													: '-',
										},
										{
											title: 'Masa Berlaku',
											render: (_, record) =>
												record.validityPeriod?.value ?? '-',
										},
									]}
									pagination={false}
								/>
								<FleetModal
									open={typeof activeIndexModal === 'number'}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									okButtonProps={{
										disabled: !values.input?.file?.id,
									}}
									onOk={async () => {
										const isValidForm = await form.validateFields([
											fieldName(['input']),
										]);
										if (isValidForm) {
											form?.submit();
										}
									}}
									onCancel={() => {
										setActiveIndexModal(undefined);
										resetFields([fieldName('input')]);
									}}
								>
									<FleetTitle>Edit Perizinan Usaha</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Jenis Dokumen"
												name={fieldName(['input', 'documentType'])}
												labelCol={{ span: 8 }}
											>
												<Input.TextArea
													rows={5}
													disabled
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="No. Dokumen"
												name={fieldName(['input', 'documentNo'])}
												labelCol={{ span: 8 }}
												rules={
													form?.getFieldValue(
														fieldName(['input', 'isDocumentNoMandatory']),
													)
														? [rule.required()]
														: []
												}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Penerbit"
												name={fieldName(['input', 'publisher'])}
												labelCol={{ span: 8 }}
												getValueFromEvent={(_, option) => option?.data}
												getValueProps={(value) => {
													if (!value) return {};
													return {
														value: {
															value: value.id,
															label: value.value,
														},
													};
												}}
											>
												<Select
													options={values.input?.publishers?.map((record) => {
														return {
															label: record.value,
															value: record.id,
															data: record,
														} as DefaultOptionType;
													})}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Tanggal Dokumen"
												name={fieldName(['input', 'documentDate'])}
												labelCol={{ span: 8 }}
												rules={[rule.required()]}
												getValueProps={(value) => ({
													value: value && dayjs(value),
												})}
												normalize={(value) =>
													value && dayjs(value).format('YYYY-MM-DD')
												}
											>
												<DatePicker
													disabledDate={(now) =>
														now > dayjs().subtract(1, 'day')
													}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Masa Berlaku"
												name={fieldName(['input', 'validityPeriod'])}
												labelCol={{ span: 8 }}
												getValueFromEvent={(_, option) => option?.data}
												getValueProps={(value) => {
													if (!value) return {};
													return {
														value: {
															value: value.id,
															label: value.value,
														},
													};
												}}
											>
												<Select
													options={values.input?.validityPeriods?.map(
														(record) => {
															return {
																label: record.value,
																value: record.id,
																data: record,
															} as DefaultOptionType;
														},
													)}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'file'])}
												noStyle
											/>
											<Form.Item
												label="Upload Dokumen"
												required
												validateStatus={
													values.input?.file?.id ? 'success' : 'error'
												}
												help={values.input?.file?.id ? '' : 'Wajib diisi'}
												labelCol={{ span: 8 }}
											>
												<Upload.Dragger
													listType="picture"
													maxCount={1}
													onRemove={() => {
														setFieldValue(fieldName(['input', 'file']), null);
													}}
													fileList={
														values.input?.file
															? [
																	{
																		name: values.input.file.name,
																		uid: values.input.file.id,
																		url: values.input.file.url,
																	} as UploadFile,
																]
															: []
													}
													beforeUpload={async (file) => {
														const fileid = Date.now().toString();
														const filepath = `customer/${customerId}/${mainInformation?.prospectId}/business-document`;

														try {
															const url = await uploadMutation.mutateAsync({
																file,
																fileid,
																filepath,
															});

															setFieldValue(fieldName(['input', 'file']), {
																format: '',
																id: fileid,
																name: file.name,
																path: filepath,
																url,
															} as BusinessDocumentItem['file']);
														} catch {
															setFieldValue(fieldName(['input', 'file']), null);
														}

														return false;
													}}
												>
													{uploadMutation.isPending ? (
														<LoadingOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													) : (
														<UploadOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													)}
												</Upload.Dragger>
											</Form.Item>
										</Col>
									</Row>
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default FRMPraSurveyInputBusinessDocumentForm;
